import React, { lazy, Suspense, useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkBreaks from 'remark-breaks';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { BookOpen, FileText, Monitor, Maximize, Minimize, ClipboardCheck, Loader2, AlertCircle, Clock, CheckCircle, XCircle } from 'lucide-react';
import HTMLContentPlayer from './HTMLContentPlayer';
import ScormPlayer from './ScormPlayer';
const CustomVideoPlayer = lazy(() => import('@/components/courses/EditCourse/CustomVideoPlayer'));
import { ContentItem } from '@/types/ai-learn.types';
import { AssessmentPlayer, AssessmentErrorBoundary } from '@/components/assessments';
import { useSearchParams } from 'react-router-dom';
import { useAssessmentAttemptState } from '@/contexts/AssessmentAttemptContext';
import { useAssessmentLearner } from '@/hooks/useAssessments';
import { loadAssessmentAttempt } from '@/utils/assessmentStorage';
import { assessmentService } from '@/services/assessment.service';
import { DetailedProgress } from '@/services/tracking.service';

function formatTimeLimit(minutes: number): string {
  if (minutes >= 60) {
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
  }
  return `${minutes} min`;
}

interface ContentDisplayProps {
  contentItem: ContentItem | null;
  selectedModule?: any;
  moduleContent?: any;
  currentVideoScene: number;
  currentAudioScene: number;
  isVideoPlaying: boolean;
  isAudioPlaying: boolean;
  videoPlaybackRate: number;
  audioPlaybackRate: number;
  videoVolume: number;
  audioVolume: number;
  autoPlayNext: boolean;
  onVideoSceneChange: (sceneIndex: number) => void;
  onAudioSceneChange: (sceneIndex: number) => void;
  onVideoPlayPause: () => void;
  onVideoPlayStateChange?: (playing: boolean) => void;
  /** Position carried across a language switch. See CustomVideoPlayer. */
  onVideoProgressRatio?: (ratio: number, playing: boolean) => void;
  videoResumeRatio?: number;
  videoResumeAutoPlay?: boolean;
  onAudioPlayPause: () => void;
  onVideoPlaybackRateChange: () => void;
  onAudioPlaybackRateChange: () => void;
  onVideoVolumeChange: (volume: number) => void;
  onAudioVolumeChange: (volume: number) => void;
  onAutoPlayToggle: () => void;
  onModuleClick?: () => void;
  onContentItemClick?: (item: ContentItem) => void;
  onProgressChange?: (progressPercentage: number) => void;
  onContentComplete?: () => void;
  /** Fired right after an assessment is submitted, so the page can re-check the
   *  course completion requirements without waiting for the next tracking tick. */
  onAssessmentSubmitted?: () => void;
  progressData?: DetailedProgress | null;
  // Admin/preview mode — assessments are shown but cannot be attempted.
  isPreview?: boolean;
}

export default function ContentDisplay({
  contentItem,
  selectedModule,
  moduleContent,
  currentVideoScene,
  currentAudioScene,
  isVideoPlaying,
  isAudioPlaying,
  videoPlaybackRate,
  audioPlaybackRate,
  videoVolume,
  audioVolume,
  autoPlayNext,
  onVideoSceneChange,
  onAudioSceneChange,
  onVideoPlayPause,
  onVideoPlayStateChange,
  onVideoProgressRatio,
  videoResumeRatio,
  videoResumeAutoPlay,
  onAudioPlayPause,
  onVideoPlaybackRateChange,
  onAudioPlaybackRateChange,
  onVideoVolumeChange,
  onAudioVolumeChange,
  onAutoPlayToggle,
  onModuleClick,
  onContentItemClick,
  onProgressChange,
  onContentComplete,
  onAssessmentSubmitted,
  progressData,
  isPreview = false
}: ContentDisplayProps) {

  const [isFullscreen, setIsFullscreen] = useState(false);

  // Helper: format seconds to HH:MM:SS
  const formatSecondsToHMS = (totalSeconds: number): string => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  // Helper: convert estimated_time Duration to HH:MM:SS
  const formatEstimatedToHMS = (et?: { hours?: number; minutes?: number }): string => {
    if (!et) return '00:00:00';
    const totalSeconds = ((et.hours || 0) * 3600) + ((et.minutes || 0) * 60);
    return formatSecondsToHMS(totalSeconds);
  };

  // Get lesson progress from tracking data
  const getLessonProgress = (lessonId: string) => {
    if (!progressData?.module_progress) return null;
    for (const mod of progressData.module_progress) {
      const lesson = mod.lesson_progress?.find(lp => lp.lesson_id === lessonId);
      if (lesson) return lesson;
    }
    return null;
  };
  const iframeContainerRef = useRef<HTMLDivElement>(null);
  // Fullscreen functionality
  const toggleFullscreen = async () => {
    if (!iframeContainerRef.current) {
      console.warn('Container ref not available for fullscreen');
      return;
    }

    try {
      if (!isFullscreen) {
        // Enter fullscreen
        if (iframeContainerRef.current.requestFullscreen) {
          await iframeContainerRef.current.requestFullscreen();
        } else if ((iframeContainerRef.current as any).webkitRequestFullscreen) {
          await (iframeContainerRef.current as any).webkitRequestFullscreen();
        } else if ((iframeContainerRef.current as any).mozRequestFullScreen) {
          await (iframeContainerRef.current as any).mozRequestFullScreen();
        } else if ((iframeContainerRef.current as any).msRequestFullscreen) {
          await (iframeContainerRef.current as any).msRequestFullscreen();
        } else {
          console.warn('Fullscreen API not supported');
          return;
        }
      } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        } else if ((document as any).mozCancelFullScreen) {
          await (document as any).mozCancelFullScreen();
        } else if ((document as any).msExitFullscreen) {
          await (document as any).msExitFullscreen();
        }
      }
    } catch (error) {
      console.error('Fullscreen toggle failed:', error);
      // Reset state if fullscreen failed
      setIsFullscreen(false);
    }
  };

  // Listen for fullscreen changes
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Preprocess content to handle escape sequences
  const preprocessContent = (content: string | undefined): string => {
    if (!content) return '';
    
    return content
      .replace(/\\n\\n/g, '\n\n')
      .replace(/\\n/g, '  \n')
      .replace(/\\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;')
      .replace(/\\r\\n/g, '  \n')
      .replace(/\\r/g, '  \n')
      .replace(/\\\\/g, '\\')
      .replace(/\\"/g, '"')
      .replace(/\\'/g, "'")
      .replace(/\\-/g, '-')
      .replace(/\\\*/g, '*')
      .replace(/\\_/g, '_');
  };

  // Markdown components with custom styling
  const markdownComponents = {
    p: ({children}: any) => <p className="text-base text-gray-700 leading-relaxed mb-4">{children}</p>,
    h1: ({children}: any) => <h1 className="text-3xl font-bold text-gray-900 mb-6 border-b border-gray-200 pb-2">{children}</h1>,
    h2: ({children}: any) => <h2 className="text-2xl font-semibold text-gray-900 mb-4 border-b border-gray-100 pb-2">{children}</h2>,
    h3: ({children}: any) => <h3 className="text-xl font-semibold text-gray-900 mb-3">{children}</h3>,
    h4: ({children}: any) => <h4 className="text-lg font-semibold text-gray-900 mb-2">{children}</h4>,
    h5: ({children}: any) => <h5 className="text-base font-semibold text-gray-900 mb-2">{children}</h5>,
    h6: ({children}: any) => <h6 className="text-sm font-semibold text-gray-900 mb-1">{children}</h6>,
    strong: ({children}: any) => <strong className="font-bold text-gray-900">{children}</strong>,
    em: ({children}: any) => <em className="italic text-gray-700">{children}</em>,
    a: ({href, children}: any) => (
      <a 
        href={href} 
        className="text-blue-600 hover:text-blue-800 underline decoration-2 underline-offset-2 hover:decoration-blue-800 transition-colors" 
        target="_blank" 
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
    code: ({inline, children}: any) => 
      inline ? (
        <code className="bg-gray-100 text-red-600 px-2 py-0.5 rounded text-sm font-mono border">
          {children}
        </code>
      ) : (
        <code className="block bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-sm overflow-x-auto">
          {children}
        </code>
      ),
    pre: ({children}: any) => (
      <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto mb-6 border border-gray-200">
        <pre className="text-gray-100 font-mono text-sm">{children}</pre>
      </div>
    ),
    blockquote: ({children}: any) => (
      <blockquote className="border-l-4 border-blue-400 bg-blue-50 text-blue-900 pl-6 py-4 my-6 italic rounded-r-lg">
        {children}
      </blockquote>
    ),
    ul: ({children}: any) => (
      <ul className="list-disc list-inside my-4 space-y-2 text-gray-700 ml-4">{children}</ul>
    ),
    ol: ({children}: any) => (
      <ol className="list-decimal list-inside my-4 space-y-2 text-gray-700 ml-4">{children}</ol>
    ),
    li: ({children}: any) => <li className="my-1 leading-relaxed">{children}</li>,
    table: ({children}: any) => (
      <div className="overflow-x-auto my-6">
        <table className="min-w-full border-collapse border border-gray-300 bg-white rounded-lg">
          {children}
        </table>
      </div>
    ),
    thead: ({children}: any) => <thead className="bg-gray-50">{children}</thead>,
    tbody: ({children}: any) => <tbody>{children}</tbody>,
    tr: ({children}: any) => <tr className="border-b border-gray-200 hover:bg-gray-50">{children}</tr>,
    th: ({children}: any) => (
      <th className="border border-gray-300 px-4 py-2 text-left font-semibold text-gray-900 bg-gray-100">
        {children}
      </th>
    ),
    td: ({children}: any) => (
      <td className="border border-gray-300 px-4 py-2 text-gray-700">
        {children}
      </td>
    ),
    hr: () => <hr className="my-8 border-t-2 border-gray-200" />,
  };
  
  if (!contentItem) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="bg-white p-12 rounded-xl shadow-sm border text-center max-w-md">
          <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-xl font-semibold text-gray-700">No Content Selected</p>
          <p className="text-sm text-gray-500 mt-2">Choose a content item from the module to start learning</p>
        </div>
      </div>
    );
  }


  // Find content item index in module
  const contentItemIndex = moduleContent?.module?.content_items?.findIndex(
    (item: ContentItem) => item._id === contentItem._id
  );

  // Calculate lesson number by counting only non-assessment items before this one
  const nonAssessmentItems = moduleContent?.module?.content_items?.filter(
    (item: ContentItem) => item.type !== 'assessment'
  ) || [];

  const lessonNumber = nonAssessmentItems.findIndex(
    (item: ContentItem) => item._id === contentItem._id
  ) + 1;

  const lessonTitle = contentItem.title;

  // Video presentation - use combined video URL with custom player
  if (contentItem.type === 'video_presentation' || contentItem.type === 'avatar_video') {
    return (
      <div className="h-full bg-gray-50 flex flex-col">
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="px-4 sm:px-6 py-3 sm:py-4">
            {selectedModule && (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm text-gray-600 mb-2 min-w-0">
                <button
                  onClick={onModuleClick}
                  className="font-medium hover:text-gray-900 transition-colors"
                  title={selectedModule.title}
                >
                  Module {selectedModule.order ?? selectedModule.module_number}
                </button>
                <span className="text-gray-400">›</span>
                <span className="font-medium">
                  Lesson {lessonNumber}
                </span>
                <span className="text-gray-400">›</span>
                <span className="text-gray-900 font-semibold break-words min-w-0">{lessonTitle}</span>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              {(contentItem.duration || contentItem.estimated_time) && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Clock className="w-4 h-4" />
                  <span>Estimated Time: {formatEstimatedToHMS(contentItem.duration || contentItem.estimated_time)}</span>
                </div>
              )}
              {(() => {
                const lessonProg = getLessonProgress(contentItem._id);
                if (!lessonProg || lessonProg.time_spent_seconds === 0) return null;
                const isCompleted = lessonProg.status === 'COMPLETED';
                return (
                  <div className={`flex items-center gap-1.5 text-xs ${isCompleted ? 'text-green-600' : 'text-blue-500'}`}>
                    {isCompleted ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                    <span>{isCompleted ? 'Completed Time' : 'Time Spent'}: {formatSecondsToHMS(lessonProg.time_spent_seconds)}</span>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
        <div className="flex-1 min-h-0 p-4">
          {contentItem.media_assets?.s3_video_url ? (
            <div className="h-full">
              <Suspense fallback={<div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 animate-spin text-purple-500" /></div>}>
                <CustomVideoPlayer
                  src={contentItem.media_assets.s3_video_url}
                  captionedSrc={contentItem.media_assets?.s3_captioned_video_url}
                  title={contentItem.title}
                  onPlayStateChange={onVideoPlayStateChange}
                  onProgressRatio={onVideoProgressRatio}
                  initialRatio={videoResumeRatio}
                  autoPlayOnResume={videoResumeAutoPlay}
                />
              </Suspense>
            </div>
          ) : (
            <div className="text-center py-16">
              <Monitor className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">Video not available</p>
              <p className="text-sm text-gray-400 mt-1">The video is currently being prepared</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // SCORM - launch the SCO in an iframe with the SCORM 1.2 runtime API
  if (contentItem.type === 'scorm') {
    return (
      <div className="h-full flex flex-col">
        {/* Breadcrumb header */}
        <div className="bg-white border-b border-gray-200 flex-shrink-0">
          <div className="px-4 sm:px-6 py-3 sm:py-4">
            {selectedModule && (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm text-gray-600 mb-2 min-w-0">
                <button
                  onClick={onModuleClick}
                  className="font-medium hover:text-gray-900 transition-colors"
                  title={selectedModule.title}
                >
                  Module {selectedModule.order ?? selectedModule.module_number}
                </button>
                <span className="text-gray-400">›</span>
                <span className="font-medium">Lesson {lessonNumber}</span>
                <span className="text-gray-400">›</span>
                <span className="text-gray-900 font-semibold break-words min-w-0">{lessonTitle}</span>
              </div>
            )}
            {/* SCORM lessons have no estimated time — show actual completed/spent time, like AI. */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            {(() => {
              const lessonProg = getLessonProgress(contentItem._id);
              if (!lessonProg || lessonProg.time_spent_seconds === 0) return null;
              const isCompleted = lessonProg.status === 'COMPLETED';
              return (
                <div className={`flex items-center gap-1.5 text-xs ${isCompleted ? 'text-green-600' : 'text-blue-500'}`}>
                  {isCompleted ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                  <span>{isCompleted ? 'Completed Time' : 'Time Spent'}: {formatSecondsToHMS(lessonProg.time_spent_seconds)}</span>
                </div>
              );
            })()}
            </div>
          </div>
        </div>
        <div className="flex-1 min-h-0">
          <ScormPlayer
            contentItem={contentItem}
            selectedModule={selectedModule}
            onProgressChange={onProgressChange}
            onContentComplete={onContentComplete}
          />
        </div>
      </div>
    );
  }

  // HTML and HTML with Audio - use HTMLContentPlayer
  if ((contentItem.type === 'html' || contentItem.type === 'html_with_audio') && contentItem.media_assets?.s3_html_url) {
    return (
      <div className="flex flex-col lg:h-full">
        {/* 1. Fixed Breadcrumb Header — one line on phones (the title truncates)
            so the lesson below keeps as much of the small screen as possible. */}
        <div className="bg-white border-b border-gray-200 flex-shrink-0">
          <div className="px-4 sm:px-6 py-2 sm:py-4">
            {selectedModule && (
              <div className="flex flex-nowrap sm:flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm text-gray-600 mb-1 sm:mb-2 min-w-0" title={lessonTitle}>
                <button
                  onClick={onModuleClick}
                  className="flex-shrink-0 whitespace-nowrap font-medium hover:text-gray-900 transition-colors"
                  title={selectedModule.title}
                >
                  Module {selectedModule.order ?? selectedModule.module_number}
                </button>
                {/* Only the title may shrink; the labels must not wrap ("Module" / "1"). */}
                <span className="flex-shrink-0 text-gray-400">›</span>
                <span className="flex-shrink-0 whitespace-nowrap font-medium">
                  Lesson {lessonNumber}
                </span>
                <span className="flex-shrink-0 text-gray-400">›</span>
                <span className="flex-1 sm:flex-initial text-gray-900 font-semibold min-w-0 truncate sm:whitespace-normal sm:break-words">{lessonTitle}</span>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 sm:mt-2">
              {(contentItem.duration || contentItem.estimated_time) && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Clock className="w-4 h-4" />
                  <span>Estimated Time: {formatEstimatedToHMS(contentItem.duration || contentItem.estimated_time)}</span>
                </div>
              )}
              {(() => {
                const lessonProg = getLessonProgress(contentItem._id);
                if (!lessonProg || lessonProg.time_spent_seconds === 0) return null;
                const isCompleted = lessonProg.status === 'COMPLETED';
                return (
                  <div className={`flex items-center gap-1.5 text-xs ${isCompleted ? 'text-green-600' : 'text-blue-500'}`}>
                    {isCompleted ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                    <span>{isCompleted ? 'Completed Time' : 'Time Spent'}: {formatSecondsToHMS(lessonProg.time_spent_seconds)}</span>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* 2. HTMLContentPlayer - renders iframe in scrollable area + controls at bottom.
            An uploaded PDF is embedded directly (non-sandboxed) via pdfUrl so
            Chrome's PDF viewer works — the sandboxed html iframe blocks it.

            Wrapped in flex-1 min-h-0 so the player gets the height LEFT UNDER
            the breadcrumb header. Its root is h-full; directly inside this
            column that meant 100% of the column plus the header, so the outer
            pane scrolled by the header's height while the iframe scrolled
            too — two nested scroll areas, and on touch screens swipes landed
            on the outer one and the lesson looked stuck.

            Phones & tablets (< lg) instead let the outline card and this
            header scroll away above the lesson (UnifiedPlayerView), so the
            player is exactly one pane tall (100cqh of that pane): once they
            are swiped off, audio bar + lesson fill the screen and nothing
            further scrolls on the outside. Browsers without container units
            (iOS < 16) get the screen minus header, tabs and Previous/Next. */}
        <div className="h-[calc(100vh-11rem)] supports-[height:100cqh]:h-[100cqh] lg:h-auto lg:flex-1 lg:min-h-0">
        <HTMLContentPlayer
          htmlUrl={contentItem.media_assets.s3_html_url}
          pdfUrl={contentItem.content_source === 'uploaded' ? contentItem.uploaded_file_url : undefined}
          audioUrl={contentItem.type === 'html_with_audio' ? contentItem.media_assets.s3_audio_url : undefined}
          title={contentItem.title}
          duration={contentItem.estimated_time?.minutes || 1}
          isPlaying={isVideoPlaying}
          playbackRate={videoPlaybackRate}
          volume={videoVolume}
          onPlayPause={onVideoPlayPause}
          onPlaybackRateChange={onVideoPlaybackRateChange}
          onVolumeChange={onVideoVolumeChange}
          onProgressChange={onProgressChange}
          onContentComplete={onContentComplete}
          onProgressRatio={onVideoProgressRatio}
          initialRatio={videoResumeRatio}
          autoPlayOnResume={videoResumeAutoPlay}
        />
        </div>
      </div>
    );
  }

  return (
    <div className="lg:h-full bg-gray-50 flex flex-col">
      {/* Content Header - Unified Style */}
      {contentItem.type !== 'assessment' && (
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="px-4 sm:px-6 py-3 sm:py-4">
            {/* Breadcrumb */}
            {selectedModule && (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm text-gray-600 mb-2 min-w-0">
                <button
                  onClick={onModuleClick}
                  className="font-medium hover:text-gray-900 transition-colors"
                  title={selectedModule.title}
                >
                  Module {selectedModule.order ?? selectedModule.module_number}
                </button>
                <span className="text-gray-400">›</span>
                <span className="font-medium">
                  Lesson {lessonNumber}
                </span>
                <span className="text-gray-400">›</span>
                <span className="text-gray-900 font-semibold break-words min-w-0">{lessonTitle}</span>
              </div>
            )}

            {/* Duration & Type Info */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              {(contentItem.duration || contentItem.estimated_time) && (
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Clock className="w-4 h-4" />
                  <span>Estimated Time: {formatEstimatedToHMS(contentItem.duration || contentItem.estimated_time)}</span>
                </div>
              )}
              {(() => {
                const lessonProg = getLessonProgress(contentItem._id);
                if (!lessonProg || lessonProg.time_spent_seconds === 0) return null;
                const isCompleted = lessonProg.status === 'COMPLETED';
                return (
                  <div className={`flex items-center gap-1.5 text-xs ${isCompleted ? 'text-green-600' : 'text-blue-500'}`}>
                    {isCompleted ? <CheckCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                    <span>{isCompleted ? 'Completed Time' : 'Time Spent'}: {formatSecondsToHMS(lessonProg.time_spent_seconds)}</span>
                  </div>
                );
              })()}
              <div className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                {contentItem.type.toUpperCase()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Content Display based on type */}
      <div className="lg:flex-1 lg:overflow-y-auto">
        {/* Description and Metadata */}
        {contentItem.type === 'assessment' && contentItem.assessment_id && (
          <AssessmentPlayerWrapper
            assessmentId={contentItem.assessment_id}
            selectedModule={selectedModule}
            moduleContent={moduleContent}
            contentItem={contentItem}
            onModuleClick={onModuleClick}
            onContentItemClick={onContentItemClick}
            isPreview={isPreview}
            onAssessmentSubmitted={onAssessmentSubmitted}
          />
        )}
      </div>
    </div>
  );
}

// Assessment Player Wrapper Component
function AssessmentPlayerWrapper({
  assessmentId,
  selectedModule,
  moduleContent,
  contentItem,
  onModuleClick,
  onContentItemClick,
  isPreview = false,
  onAssessmentSubmitted
}: {
  assessmentId: string;
  selectedModule?: any;
  moduleContent?: any;
  contentItem: ContentItem;
  onModuleClick?: () => void;
  onContentItemClick?: (item: ContentItem) => void;
  isPreview?: boolean;
  onAssessmentSubmitted?: () => void;
}) {
  // The language the learner is reading, taken from the URL so it matches
  // whatever the course player is showing. Undefined means the original.
  const [assessmentSearchParams] = useSearchParams();
  const activeLanguageCode = assessmentSearchParams.get('language') || undefined;

  // Lets the course player hold the language steady while a paper is open.
  const { setAttemptInProgress } = useAssessmentAttemptState();

  const { data: assessment, isLoading, error } = useAssessmentLearner(
    assessmentId,
    activeLanguageCode
  );
  const savedAttempt = loadAssessmentAttempt(assessmentId);
  const [assessmentResult, setAssessmentResult] = React.useState<any>(null);
  // Auto-resume a saved attempt — but never in preview (admin can't attempt).
  const [hasStarted, setHasStarted] = React.useState(!!savedAttempt && !isPreview);
  const [userSummary, setUserSummary] = React.useState<any>(null);
  const [loadingSummary, setLoadingSummary] = React.useState(true);
  const [startedAttemptId, setStartedAttemptId] = React.useState<string | null>(null);
  const [startError, setStartError] = React.useState<string | null>(null);
  const [isStarting, setIsStarting] = React.useState(false);

  // Reset state when assessmentId changes (switching to a different assessment)
  React.useEffect(() => {
    const newSavedAttempt = loadAssessmentAttempt(assessmentId);
    setAssessmentResult(null);
    setHasStarted(!!newSavedAttempt && !isPreview);
    setUserSummary(null);
    setLoadingSummary(true);
    setStartedAttemptId(null);
    setStartError(null);
    setIsStarting(false);
  }, [assessmentId]);

  // Fetch user summary to check attempts
  React.useEffect(() => {
    const fetchSummary = async () => {
      try {
        const summary = await assessmentService.getUserAssessmentSummary(assessmentId);
        setUserSummary(summary);
      } catch (error) {
        console.error('Failed to fetch assessment summary:', error);
      } finally {
        setLoadingSummary(false);
      }
    };

    fetchSummary();
  }, [assessmentId]);

  // An attempt is open while the questions are on screen and not yet
  // submitted — this also covers resuming a saved attempt and leaving with
  // Exit Assessment, which the start/submit calls alone did not.
  const isAnswering = hasStarted && !assessmentResult && !isPreview;
  React.useEffect(() => {
    setAttemptInProgress(isAnswering);
  }, [isAnswering, setAttemptInProgress]);
  React.useEffect(() => () => setAttemptInProgress(false), [setAttemptInProgress]);

  // Get only non-assessment content items (actual lessons)
  const nonAssessmentItems = moduleContent?.module?.content_items?.filter(
    (item: ContentItem) => item.type !== 'assessment'
  ) || [];

  // Find which lesson this assessment belongs to
  let lessonContentItem: ContentItem | null = null;
  let lessonNumber = 0;

  const allItems = moduleContent?.module?.content_items || [];
  const assessmentIndex = allItems.findIndex((item: ContentItem) => item._id === contentItem._id);

  if (contentItem.assessment_type === 'POST') {
    // POST assessment: find the previous non-assessment item
    for (let i = assessmentIndex - 1; i >= 0; i--) {
      if (allItems[i].type !== 'assessment') {
        lessonContentItem = allItems[i];
        break;
      }
    }
  } else if (contentItem.assessment_type === 'PRE') {
    // PRE assessment: find the next non-assessment item
    for (let i = assessmentIndex + 1; i < allItems.length; i++) {
      if (allItems[i].type !== 'assessment') {
        lessonContentItem = allItems[i];
        break;
      }
    }
  }

  // Get lesson number by counting non-assessment items before the found lesson
  if (lessonContentItem) {
    lessonNumber = nonAssessmentItems.findIndex((item: ContentItem) => item._id === lessonContentItem._id) + 1;
  }

  if (isLoading || loadingSummary) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading assessment...</p>
        </div>
      </div>
    );
  }

  if (error || !assessment) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
        <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Assessment</h3>
        <p className="text-gray-600">
          {error?.message || 'The assessment could not be loaded. Please try again later.'}
        </p>
      </div>
    );
  }

  // Handle start assessment
  const handleStartAssessment = async () => {
    // Preview (admin) mode: open the questions read-only, never start a
    // backend attempt.
    if (isPreview) {
      setHasStarted(true);
      return;
    }
    setIsStarting(true);
    setStartError(null);

    try {
      // The language is recorded ON the attempt, so which wording the learner
      // sat the paper in is a fact about the attempt rather than an editable
      // parameter. Grading always uses the master, so this never moves a score.
      const attemptResponse = await assessmentService.startAttempt(
        assessmentId,
        activeLanguageCode
      );
      setStartedAttemptId(attemptResponse.id);
      // The attempt is now open: the language it was started in is recorded on
      // it, and the switcher stays fixed until this paper is submitted
      // (isAnswering above).
      setHasStarted(true);
    } catch (error: any) {
      console.error('Failed to start assessment:', error);
      const errorMessage = error.response?.data?.error?.error_description ||
                          error.response?.data?.message ||
                          error.message ||
                          'Failed to start assessment. Please try again.';
      setStartError(errorMessage);

      // If max attempts reached, reload summary to update UI
      if (error.response?.data?.error?.error === 'MAX_ATTEMPTS_REACHED') {
        try {
          const summary = await assessmentService.getUserAssessmentSummary(assessmentId);
          setUserSummary(summary);
        } catch (summaryError) {
          console.error('Failed to reload summary:', summaryError);
        }
      }
    } finally {
      setIsStarting(false);
    }
  };

  // Show start screen if not started yet
  if (!hasStarted && !assessmentResult) {
    const totalAttempts = userSummary?.total_attempts ?? 0;
    const is_unlimited = !assessment.max_attempts;
    const remainingAttempts = is_unlimited
      ? null
      : Math.max(0, (userSummary?.remaining_attempts ?? assessment.max_attempts ?? 0));
    const canRetake = assessment.allow_retry === false && totalAttempts > 0
      ? false
      : (userSummary?.can_retake ?? true);

    return (
      <div className="lg:h-full bg-gray-50 flex flex-col lg:overflow-y-auto p-4 sm:p-6">
        <div className="max-w-4xl mx-auto w-full">
          {/* Error Banner */}
          {startError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-900 mb-1">Cannot Start Assessment</p>
                  <p className="text-xs text-red-700">{startError}</p>
                </div>
                <button
                  onClick={() => setStartError(null)}
                  className="text-red-600 hover:text-red-800"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* Assessment Info Card */}
          <div className="bg-white rounded-xl border border-gray-200 p-5 sm:p-8 mb-6">
            {/* Phones: icon above the title so the title and description use the full width */}
            <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 mb-5 sm:mb-6">
              <div className="p-2.5 sm:p-3 bg-purple-100 rounded-lg flex-shrink-0">
                <ClipboardCheck className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 break-words">{assessment.title}</h2>
                {assessment.description && (
                  <p className="text-sm sm:text-base text-gray-600 break-words">{assessment.description}</p>
                )}
              </div>
            </div>

            {/* Assessment Details */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                <p className="text-xs text-gray-500 mb-1">Questions</p>
                <p className="text-lg font-semibold text-gray-900">{assessment.total_questions}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                <p className="text-xs text-gray-500 mb-1">Total Points</p>
                <p className="text-lg font-semibold text-gray-900">{assessment.total_points}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                <p className="text-xs text-gray-500 mb-1">Passing Score</p>
                <p className="text-lg font-semibold text-gray-900">{assessment.passing_score}%</p>
              </div>
              {assessment.time_limit_minutes && (
                <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                  <p className="text-xs text-gray-500 mb-1">Time Limit</p>
                  <p className="text-lg font-semibold text-gray-900">{formatTimeLimit(assessment.time_limit_minutes)}</p>
                </div>
              )}
            </div>

            {/* Attempt Information */}
            {userSummary && (
              <div className="border-t border-gray-200 pt-6 mb-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">Your Progress</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Attempts Used</p>
                    <p className="text-sm font-medium text-gray-900">
                      {totalAttempts} / {is_unlimited ? 'Unlimited' : assessment.max_attempts}
                    </p>
                  </div>
                  {userSummary.best_score !== undefined && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Best Score</p>
                      <p className="text-sm font-medium text-gray-900">
                        {typeof userSummary.best_percentage === 'number'
                          ? userSummary.best_percentage === 0
                            ? '0'
                            : userSummary.best_percentage % 1 === 0
                            ? userSummary.best_percentage.toFixed(0)
                            : userSummary.best_percentage.toFixed(2)
                          : userSummary.best_percentage}%
                      </p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Remaining Attempts</p>
                    <p className="text-sm font-medium text-gray-900">
                      {is_unlimited ? 'Unlimited' : remainingAttempts}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Start Button or Max Attempts Message */}
            {isPreview ? (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-900 mb-1">Preview Mode</p>
                      <p className="text-xs text-blue-700">
                        You can view the questions, but answering and submitting are disabled.
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  onClick={handleStartAssessment}
                  className="w-full px-6 py-3 text-base font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  View Questions
                </button>
              </>
            ) : !canRetake ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    {assessment.allow_retry === false ? (
                      <>
                        <p className="text-sm font-medium text-amber-900 mb-1">
                          Retry Not Allowed
                        </p>
                        <p className="text-xs text-amber-700">
                          Retaking this assessment is not allowed.
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-sm font-medium text-amber-900 mb-1">
                          Maximum Attempts Reached
                        </p>
                        <p className="text-xs text-amber-700">
                          You have used all {assessment.max_attempts} attempts for this assessment.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <button
                onClick={handleStartAssessment}
                disabled={isStarting}
                className="w-full px-6 py-3 text-base font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isStarting ? 'Starting...' : totalAttempts > 0 ? 'Retake Assessment' : 'Start Assessment'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show results if assessment was submitted
  if (assessmentResult) {
    return (
      <div className="lg:h-full bg-gray-50 flex flex-col lg:overflow-y-auto p-4 sm:p-6">
        <div className="max-w-4xl mx-auto w-full">
          {/* Results Header */}
          <div className={`rounded-xl border-2 p-5 sm:p-8 text-center mb-6 ${
            assessmentResult.passed
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          }`}>
            <div className="mb-4">
              {assessmentResult.passed ? (
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto" />
              ) : (
                <XCircle className="w-16 h-16 text-red-600 mx-auto" />
              )}
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {assessmentResult.passed ? 'Assessment Passed!' : 'Assessment Not Passed'}
            </h2>
            <p className="text-lg text-gray-700 mb-4">
              Your Score: {assessmentResult.score} / {assessmentResult.max_score} ({assessmentResult.percentage.toFixed(2)}%)
            </p>
            <p className="text-sm text-gray-600">
              Time Spent: {Math.floor(assessmentResult.time_spent_seconds / 60)}m {assessmentResult.time_spent_seconds % 60}s
            </p>
          </div>

          {/* Question Results */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 mb-4 sm:mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Question Results</h3>
            <div className="space-y-4">
              {assessmentResult.answers.map((answer: any, index: number) => (
                <div
                  key={answer.question_id}
                  className={`p-4 rounded-lg border ${
                    answer.is_correct
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {answer.is_correct ? (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-gray-900">Question {index + 1}</p>
                        <span className="text-xs text-gray-500">
                          ({answer.points_earned} / {answer.points_possible} pts)
                        </span>
                      </div>

                      {/* Question Text */}
                      {answer.question_text && (
                        <p className="text-sm text-gray-700 mb-2">{answer.question_text}</p>
                      )}

                      {/* User's Answer */}
                      {answer.selected_option_ids?.length > 0 && answer.options && (
                        <div className="text-sm mb-2">
                          <span className="font-medium text-gray-600">Your answer: </span>
                          <span className={answer.is_correct ? 'text-green-700' : 'text-red-700'}>
                            {answer.options
                              .filter((opt: any) => answer.selected_option_ids.includes(opt.id))
                              .map((opt: any) => opt.text)
                              .join(', ') || 'No answer'}
                          </span>
                        </div>
                      )}
                      {answer.text_answer && (
                        <div className="text-sm mb-2">
                          <span className="font-medium text-gray-600">Your answer: </span>
                          <span className={answer.is_correct ? 'text-green-700' : 'text-red-700'}>
                            {answer.text_answer}
                          </span>
                        </div>
                      )}

                      {/* Correct Answer (show_correct_answers) */}
                      {!answer.is_correct && answer.correct_option_ids && answer.options && (
                        <div className="text-sm mb-2">
                          <span className="font-medium text-gray-600">Correct answer: </span>
                          <span className="text-green-700">
                            {answer.options
                              .filter((opt: any) => answer.correct_option_ids.includes(opt.id))
                              .map((opt: any) => opt.text)
                              .join(', ')}
                          </span>
                        </div>
                      )}
                      {!answer.is_correct && answer.correct_text_answer && (
                        <div className="text-sm mb-2">
                          <span className="font-medium text-gray-600">Correct answer: </span>
                          <span className="text-green-700">{answer.correct_text_answer}</span>
                        </div>
                      )}
                      {!answer.is_correct && answer.answer_keywords?.length > 0 && (
                        <div className="text-sm mb-2">
                          <span className="font-medium text-gray-600">Expected keywords: </span>
                          <span className="text-green-700">{answer.answer_keywords.join(', ')}</span>
                        </div>
                      )}

                      {/* Explanation (show_feedback) */}
                      {answer.explanation && (
                        <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-xs font-semibold text-blue-700 mb-1">Explanation</p>
                          <p className="text-sm text-blue-900">{answer.explanation}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4">
            <button
              onClick={() => {
                setAssessmentResult(null);
                setHasStarted(false);
                // Reload summary
                assessmentService.getUserAssessmentSummary(assessmentId)
                  .then(setUserSummary)
                  .catch(console.error);
              }}
              className="flex-1 px-6 py-3 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
            >
              Back to Assessment Info
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AssessmentErrorBoundary>
      <AssessmentPlayer
        assessment={assessment}
        attemptId={isPreview ? undefined : (startedAttemptId || undefined)}
        readOnly={isPreview}
        onSubmit={(result) => {
          // Paper submitted — the learner may change language again (isAnswering).
          setAssessmentResult(result);
          // Passing this may have been the last thing gating course completion —
          // let the page re-check instead of waiting for the next tracking tick.
          if (!isPreview) onAssessmentSubmitted?.();
        }}
        onExit={(submission) => {
          // If submission is provided (exit with submit), track it but don't show results
          if (submission) {
            console.log('Assessment submitted on exit:', submission);
          }

          // Reset to show the assessment details popup again
          setHasStarted(false);
          setStartedAttemptId(null);

          // Reload summary to refresh attempts and scores
          assessmentService.getUserAssessmentSummary(assessmentId)
            .then(setUserSummary)
            .catch(console.error);
        }}
        savedAttempt={isPreview ? undefined : (savedAttempt || undefined)}
        breadcrumb={selectedModule && lessonContentItem && lessonNumber > 0 ? {
          moduleNumber: selectedModule.order ?? selectedModule.module_number,
          moduleTitle: selectedModule.title,
          lessonNumber: lessonNumber,
          lessonTitle: lessonContentItem.title,
          onModuleClick: onModuleClick,
          onLessonClick: () => {
            if (lessonContentItem) {
              onContentItemClick?.(lessonContentItem);
            }
          }
        } : undefined}
      />
    </AssessmentErrorBoundary>
  );
}